import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckIcon, ZapIcon, SparklesIcon } from 'lucide-react';
const plans = [{
  name: 'Self-Paced',
  price: 50,
  originalPrice: 100,
  description: 'Learn at your own pace with an ever-growing library and mentor support',
  features: ['Flexible, self-paced learning — no deadlines', 'Ever-growing course library, always expanding', '48+ hours of video content', '8 portfolio projects', 'Mentor access with career support', 'Community Discord access', 'Weekly live Q&A sessions', 'Certificate of completion', 'Lifetime access to all materials'],
  notIncluded: ['Live cohort sessions', 'Weekly group critiques', '1-on-1 mentorship sessions'],
  cta: 'Get Started',
  popular: false,
  installments: false
}, {
  name: 'Pro Cohort',
  price: 200,
  originalPrice: 400,
  description: 'Our most popular option with live support and guaranteed results',
  features: ['Everything in Self-Paced', '12 live cohort sessions', 'Weekly live Q&A sessions', 'Weekly group critiques', 'Private Slack community', 'Portfolio review sessions', '3 months of office hours', '8 hours of 1-on-1 mentorship', 'Resume & LinkedIn optimization', 'Interview preparation', 'Job placement guarantee', 'Lifetime alumni network access'],
  notIncluded: [],
  cta: 'Join Next Cohort',
  popular: true,
  installments: true
}];
export function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="pricing" className="py-24 bg-dark-900 relative" data-id="element-461">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-800 to-dark-900" data-id="element-462" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref} data-id="element-463">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-464">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} className="inline-block text-brand-yellow text-sm font-semibold tracking-wider uppercase mb-4" data-id="element-465">
            Investment
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-466">
            Choose your path to
            <br data-id="element-467" />
            <span className="text-gradient-yellow" data-id="element-468">design mastery</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-469">
            All plans include a 14-day money-back guarantee. No questions asked.
          </motion.p>
        </div>

        {/* Urgency Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="flex items-center justify-center gap-3 mb-12 p-4 rounded-xl bg-brand-yellow/10 border border-brand-yellow/20" data-id="element-470">
          <span className="text-brand-yellow font-medium" data-id="element-471">
            🔥 April cohort: Only 10 spots left — each cohort admits just 10
            students
          </span>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start max-w-4xl mx-auto" data-id="element-472">
          {plans.map((plan, index) => <motion.div key={plan.name} initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2 + index * 0.1
        }} className={`relative rounded-2xl p-8 ${plan.popular ? 'bg-gradient-to-b from-brand-yellow/10 to-dark-800 border-2 border-brand-yellow/50 lg:scale-110 z-10' : 'bg-dark-800/50 border border-white/10'}`} data-id="element-473">
              {/* Popular badge */}
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-474">
                  <span className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-brand-yellow text-dark-900 text-sm font-semibold" data-id="element-475">
                    <SparklesIcon className="w-4 h-4" data-id="element-476" />
                    Most Popular
                  </span>
                </div>}

              {/* Plan header */}
              <div className="text-center mb-8" data-id="element-477">
                <h3 className="text-xl font-semibold text-white mb-2" data-id="element-478">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-400 mb-4" data-id="element-479">{plan.description}</p>
                <div className="flex items-center justify-center gap-2" data-id="element-480">
                  <span className="text-5xl font-bold text-white" data-id="element-481">
                    ${plan.price}
                  </span>
                  <div className="text-left" data-id="element-482">
                    <span className="block text-sm text-gray-500 line-through" data-id="element-483">
                      ${plan.originalPrice}
                    </span>
                    <span className="block text-xs text-brand-yellow font-medium" data-id="element-484">
                      Save ${plan.originalPrice - plan.price}
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8" data-id="element-485">
                {plan.features.map(feature => <li key={feature} className="flex items-start gap-3" data-id="element-486">
                    <CheckIcon className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-487" />
                    <span className="text-gray-300" data-id="element-488">{feature}</span>
                  </li>)}
                {plan.notIncluded.map(feature => <li key={feature} className="flex items-start gap-3 opacity-50" data-id="element-489">
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-490">
                      <span className="w-1.5 h-0.5 bg-gray-500 rounded" data-id="element-491" />
                    </span>
                    <span className="text-gray-500" data-id="element-492">{feature}</span>
                  </li>)}
              </ul>

              {/* CTA */}
              <button className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 ${plan.popular ? 'bg-brand-yellow text-dark-900 hover:bg-brand-yellow-dark hover:scale-105 glow-yellow' : 'bg-white/10 text-white hover:bg-white/20'}`} data-id="element-493">
                {plan.cta}
              </button>

              {/* Payment options */}
              {plan.installments && <p className="text-center text-sm text-gray-500 mt-4" data-id="element-494">
                  You can make 2 payments of $100
                </p>}
            </motion.div>)}
        </div>

        {/* Trust elements */}
      </div>
    </section>;
}